import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of } from "rxjs";
import { catchError, finalize, map, tap } from "rxjs/operators";
import {
  IProgram,
  IProgramListItem,
  IProgramListReqParams,
  AppLoadError,
} from "../shared/models";
import { campusNameConverter, campusUrlConverter } from "../utils";
import { ProgramListService } from "../shared/services/api/program-list.service";
import { SharedStoreService } from "./shared-store.service";
import { GtmService } from "../shared/services/gtm.service";

@Injectable({
  providedIn: "root",
})
export class ProgramListStoreService {
  private readonly _programList = new BehaviorSubject<IProgramListItem | null>(
    null
  );
  private readonly _loading = new BehaviorSubject<boolean>(false);
  private readonly _error = new BehaviorSubject<AppLoadError | null>(null);

  programList$ = this._programList.asObservable();
  loading$ = this._loading.asObservable();
  error$ = this._error.asObservable();

  get programList() {
    return this._programList.getValue();
  }

  constructor(
    private service: ProgramListService,
    private sharedStore: SharedStoreService,
    private gtmService: GtmService
  ) {}

  // --- Selectors ---

  // Select the current program list item
  selectProgramList(): Observable<IProgramListItem | null> {
    return this.programList$.pipe(map((programList) => programList || null));
  }

  // Select the error state
  selectError(): Observable<AppLoadError | null> {
    return this.error$.pipe(map((error) => error));
  }

  // Select the loading state
  selectLoading(): Observable<boolean> {
    return this.loading$.pipe(map((loading) => loading));
  }

  // Select a specific program by code
  selectProgramByCode(programCode: string): Observable<IProgram | null> {
    return this.programList$.pipe(
      map((programList) => {
        if (!programList) return null;
        return (
          programList.list.find(
            (program) => program.programCode === programCode
          ) || null
        );
      })
    );
  }

  // Select program item by index (ID)
  selectProgramItemById(id: string): Observable<IProgramListItem | null> {
    return this.programList$.pipe(
      map((programList) =>
        programList && programList.id === id ? programList : null
      )
    );
  }

  /**
   * @GET
   */
  getProgramList(
    reqParams: IProgramListReqParams
  ): Observable<IProgramListItem> {
    this._loading.next(true);
    return this.service.getProgramLists(reqParams).pipe(
      map((programs: IProgram[]) => {
        programs.forEach((program) => {
          if (program.eligibility) {
            program.eligibility = program.eligibility.filter((el) => el);
          }
          program.campus = campusNameConverter(program.campus);
          program.campusUrl = campusUrlConverter(program.campus);
        });

        return {
          id: reqParams.id,
          list: programs,
          visa: reqParams.visa,
          year: reqParams.year,
          month: reqParams.month,
        } as IProgramListItem;
      }),
      tap((programListItem: IProgramListItem) => {
        this._programList.next(programListItem);
        this._error.next(null);
      }),
      catchError((error) => {
        const displayError =
          error instanceof Error
            ? new AppLoadError(error.name, error.message)
            : new AppLoadError();
        this._error.next(displayError);
        return of(null);
      }),
      finalize(() => this._loading.next(false)) // Stop loading when complete
    );
  }

  /**
   * This is trigger after clicking continue in the step
   */
  viewProgramListSuccess(): void {
    this.gtmService.pushTag({
      event: "international_application_progress",
      step_number: "2",
      step_name: "program-availability",
    });
    this.sharedStore.triggerNavigationAfterSubmit("PROGRAM_LIST");
  }
}
